'use client';

import { Mail, MessageSquare, PhoneCall, PhoneIncoming } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useState } from 'react';
import type { ElasticBranch } from '@//app/api/search/branch/[id]';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Button } from '@//components/Button';
import { PopoverMenu, PopoverMenuButton } from '@//components/PopoverMenu';

const CallModal = lazy(() => import('./CallModal'));
const CallMeModal = lazy(() => import('./CallMeModal'));
const MailModal = lazy(() => import('./MailModal'));
const MessageModal = lazy(() => import('./MessageModal'));

interface ContactButtonProps {
  variant: 'cta' | 'secondary';
  vehicle: ElasticVehicle;
  branch: ElasticBranch;
}

export const ContactButton: React.FC<ContactButtonProps> = ({ variant, vehicle, branch }) => {
  const t = useTranslations('pages.item.informationSection');
  const [isOpen, setIsOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callMeModalOpen, setCallMeModalOpen] = useState(false);

  const baseLeadData = {
    branchId: vehicle.branches?.[0]?.id ?? '',
    metadata: {
      itemForSaleId: vehicle.id,
    },
  };

  const hasPhoneNumber = !!(
    vehicle.contactOptions?.telephone ||
    branch.defaultContactOptions?.telephone ||
    branch.telephoneNumber
  );
  // Toggle CTA variants. Will be used for A/B testing in the future.
  const showMultipleCta = true;

  return (
    <>
      <Suspense fallback={null}>
        <MessageModal
          isOpen={messageModalOpen}
          baseLeadData={baseLeadData}
          vehicle={vehicle}
          branch={branch}
          onClose={() => setMessageModalOpen(false)}
        />
      </Suspense>
      <Suspense fallback={null}>
        <MailModal
          isOpen={mailModalOpen}
          baseLeadData={baseLeadData}
          vehicle={vehicle}
          onClose={() => setMailModalOpen(false)}
        />
      </Suspense>
      <Suspense fallback={null}>
        <CallModal
          vehicle={vehicle}
          branch={branch}
          isOpen={callModalOpen}
          onClose={() => setCallModalOpen(false)}
        />
      </Suspense>
      <Suspense fallback={null}>
        <CallMeModal
          isOpen={callMeModalOpen}
          baseLeadData={baseLeadData}
          vehicle={vehicle}
          onClose={() => setCallMeModalOpen(false)}
        />
      </Suspense>
      {showMultipleCta ? (
        <>
          <Button
            sizeLtMd="lg"
            sizeMd="xl"
            variant="cta"
            fullWidth
            onClick={() => setMessageModalOpen(true)}
            title={t('contact.contactButton.popover.message.title')}
            testId="contact-button-message"
          >
            {t('contact.contactButton.popover.message.label')}
          </Button>
          <Button
            sizeLtMd="lg"
            sizeMd="xl"
            variant="cta"
            fullWidth
            onClick={() => setMailModalOpen(true)}
            title={t('contact.contactButton.popover.mail.title')}
            testId="contact-button-mail"
          >
            {t('contact.contactButton.popover.mail.label')}
          </Button>
          {hasPhoneNumber && (
            <Button
              sizeLtMd="lg"
              sizeMd="xl"
              variant="cta"
              fullWidth
              onClick={() => setCallModalOpen(true)}
              title={t('contact.contactButton.popover.call.title')}
              testId="contact-button-call"
            >
              {t('contact.contactButton.popover.call.label')}
            </Button>
          )}
          <Button
            sizeLtMd="lg"
            sizeMd="xl"
            variant="cta"
            fullWidth
            onClick={() => setCallMeModalOpen(true)}
            title={t('contact.contactButton.popover.callUp.title')}
            testId="contact-button-callup"
          >
            {t('contact.contactButton.popover.callUp.label')}
          </Button>
        </>
      ) : (
        <PopoverMenu
          sideOffset={8}
          side="bottom"
          align="center"
          width="anchorWidth"
          ariaLabel={t('contact.contactButton.popover.ariaLabel')}
          open={isOpen}
          onOpenChange={setIsOpen}
          trigger={
            <Button
              title={t('contact.contactButton.title')}
              sizeLtMd="lg"
              sizeMd="xl"
              fullWidth
              variant={variant}
              testId="contact-button"
            >
              {t('contact.contactButton.label')}
            </Button>
          }
        >
          {[
            <PopoverMenuButton
              key="message-cta-button"
              label={t('contact.contactButton.popover.message.label')}
              title={t('contact.contactButton.popover.message.title')}
              icon={<MessageSquare aria-hidden="true" />}
              onClick={() => setMessageModalOpen(true)}
              testId="contact-button-message"
            />,
            <PopoverMenuButton
              key="mail-cta-button"
              label={t('contact.contactButton.popover.mail.label')}
              title={t('contact.contactButton.popover.mail.title')}
              icon={<Mail aria-hidden="true" />}
              onClick={() => setMailModalOpen(true)}
              testId="contact-button-mail"
            />,
            ...(hasPhoneNumber
              ? [
                  <PopoverMenuButton
                    key="call-cta-button"
                    label={t('contact.contactButton.popover.call.label')}
                    title={t('contact.contactButton.popover.call.title')}
                    icon={<PhoneCall aria-hidden="true" />}
                    onClick={() => setCallModalOpen(true)}
                    testId="contact-button-call"
                  />,
                ]
              : []),
            <PopoverMenuButton
              key="callup-cta-button"
              label={t('contact.contactButton.popover.callUp.label')}
              title={t('contact.contactButton.popover.callUp.title')}
              icon={<PhoneIncoming aria-hidden="true" />}
              onClick={() => setCallMeModalOpen(true)}
              testId="contact-button-callup"
            />,
          ]}
        </PopoverMenu>
      )}
    </>
  );
};
